<div class="category-management">
  <!-- Header Section -->
  <div class="page-header">
    <div class="header-content">
      <div class="header-left">
        <h1 class="page-title">
          <i class="fas fa-tags"></i>
          Category Management
        </h1>
        <p class="page-subtitle">Manage product categories and pricing</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" (click)="openAddModal(addCategoryModal)">
          <i class="fas fa-plus"></i>
          Add New Category
        </button>
      </div>
    </div>
  </div>

  <!-- Search and Filters Section -->
  <div class="filters-section">
    <div class="filters-grid">
      <div class="search-box">
        <div class="input-group">
          <span class="input-group-text">
            <i class="fas fa-search"></i>
          </span>
          <input type="text" class="form-control" placeholder="Search categories by name or sub-products..."
            [(ngModel)]="searchTerm" (input)="filterCategories()">
        </div>
      </div>

      <div class="filter-controls">
        <select class="form-select" [(ngModel)]="selectedProductId" (change)="filterCategories()">
          <option value="all">All Products</option>
          @for (product of products; track product.id) {
            <option [value]="product.id">{{ product.name }}</option>
          }
        </select>

        <select class="form-select" [(ngModel)]="selectedStatus" (change)="filterCategories()">
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  @if (loading) {
  <div class="loading-state">
    <div class="spinner"></div>
    <p>Loading categories...</p>
  </div>
  }

  <!-- Error State -->
  @if (error && !loading) {
  <div class="error-state">
    <div class="error-content">
      <i class="fas fa-exclamation-triangle fa-2x"></i>
      <h3>Failed to load categories</h3>
      <p>{{ error }}</p>
      <button class="btn btn-primary" (click)="loadCategories()">
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>
  </div>
  }

  <!-- Categories Table -->
  @if (!loading && !error) {
  <div class="categories-table-container">
    <div class="table-header">
      <div class="table-info">
        <span class="total-count">{{ filteredCategories.length }} categories found</span>
        @if (filteredCategories.length > itemsPerPage) {
        <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        }
      </div>
    </div>

    @if (filteredCategories.length === 0) {
    <div class="no-categories">
      <i class="fas fa-tags fa-3x"></i>
      <h3>No categories found</h3>
      <p>Try adjusting your search criteria or add a new category.</p>
      <button class="btn btn-primary" (click)="openAddModal(addCategoryModal)">
        <i class="fas fa-plus"></i>
        Add First Category
      </button>
    </div>
    } @else {
    <div class="table-responsive">
      <table class="table table-hover categories-table">
        <thead>
          <tr>
            <th class="col-category-name">Category Name</th>
            <th class="col-product">Product</th>
            <th class="col-price">Price</th>
            <th class="col-sub-products">Sub-Products</th>
            <th class="col-status">Status</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          @for (category of paginatedCategories; track category.id) {
          <tr class="category-row">
            <td class="category-info">
              <div class="category-details">
                <h4 class="category-name">{{ category.name }}</h4>
                <span class="category-id">
                  <i class="fas fa-hashtag me-1"></i>
                  ID: {{ category.id }}
                </span>
              </div>
            </td>

            <td class="product-info">
              <div class="product-list">
                @for (productId of getCategoryProductIds(category); track productId) {
                  <div class="product-item">
                    <span class="product-name">{{ getProductName(productId) }}</span>
                  </div>
                } @empty {
                  <span class="no-products">No Products</span>
                }
              </div>
            </td>

            <td class="price-info">
              <div class="current-price">₹{{ category.price }}</div>
              @if (category.originalPrice && category.originalPrice > category.price) {
              <div class="original-price">₹{{ category.originalPrice }}</div>
              }
            </td>

            <td class="sub-products-info">
              <div class="sub-products-list">
                {{ formatSubProductsForDisplay(category.subProducts) }}
              </div>
            </td>

            <td class="status-info">
              <div class="status-badge" [ngClass]="category.isActive ? 'active' : 'inactive'">
                <i [class]="category.isActive ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"></i>
                {{ category.isActive ? 'Active' : 'Inactive' }}
              </div>
            </td>

            <td class="actions">
              <div class="action-buttons">
                <button class="btn btn-sm btn-outline-primary" (click)="openEditModal(editCategoryModal, category)"
                  title="Edit Category">
                  <i class="fas fa-edit"></i>
                </button>

                <button class="btn btn-sm btn-outline-danger" (click)="openDeleteModal(deleteCategoryModal, category)"
                  title="Delete Category">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    @if (totalPages > 1) {
    <div class="pagination-container">
      <nav aria-label="Categories pagination">
        <ul class="pagination justify-content-center">
          <li class="page-item" [class.disabled]="currentPage === 1">
            <button class="page-link" (click)="changePage(currentPage - 1)" [disabled]="currentPage === 1">
              <i class="fas fa-chevron-left"></i>
            </button>
          </li>

          @for (page of [].constructor(totalPages); track $index) {
          <li class="page-item" [class.active]="($index + 1) === currentPage">
            <button class="page-link" (click)="changePage($index + 1)">
              {{ $index + 1 }}
            </button>
          </li>
          }

          <li class="page-item" [class.disabled]="currentPage === totalPages">
            <button class="page-link" (click)="changePage(currentPage + 1)" [disabled]="currentPage === totalPages">
              <i class="fas fa-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>
    }
    }
  </div>
  }

  <!-- Add/Edit Category Modal -->
  <ng-template #addCategoryModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-plus"></i>
        {{ isEditing ? 'Edit Category' : 'Add New Category' }}
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    
    @if (categoryForm) {
    <div class="modal-body">
      <form [formGroup]="categoryForm" (ngSubmit)="saveCategory()">
        <div class="row">
          <!-- Basic Information -->
          <div class="col-12">
            <h5 class="form-section-title">Basic Information</h5>

            <div class="mb-3">
              <label class="form-label">Category Name *</label>
              <input type="text" class="form-control" formControlName="name" placeholder="Enter category name">
              @if (categoryForm.get('name')?.invalid && categoryForm.get('name')?.touched) {
              <div class="invalid-feedback">
                Category name is required and must be at least 2 characters.
              </div>
              }
            </div>

            <div class="mb-3">
              <label class="form-label">Products *</label>
              <div class="product-selection">
                @if (products.length === 0) {
                  <div class="alert alert-warning py-2">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    No products available. Please create products first.
                  </div>
                } @else {
                  <div class="product-checkboxes">
                    @for (product of products; track product.id) {
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          [id]="'product-' + product.id"
                          [value]="product.id"
                          [checked]="isProductSelected(product.id)"
                          (change)="toggleProduct(product.id, $event)">
                        <label class="form-check-label" [for]="'product-' + product.id">
                          <strong>{{ product.name }}</strong>
                          @if (product.doseType) {
                            <small class="text-muted ms-2">({{ product.doseType }})</small>
                          }
                        </label>
                      </div>
                    }
                  </div>
                  @if (categoryForm.get('productIds')?.invalid && categoryForm.get('productIds')?.touched) {
                    <div class="invalid-feedback d-block">
                      Please select at least one product.
                    </div>
                  }
                  <small class="form-text text-muted">Select one or more products for this category</small>
                }
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Price (₹) *</label>
                  <input type="number" class="form-control" formControlName="price" placeholder="0.00" step="0.01">
                  @if (categoryForm.get('price')?.invalid && categoryForm.get('price')?.touched) {
                  <div class="invalid-feedback">
                    Price is required and must be greater than 0.
                  </div>
                  }
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Original Price (₹)</label>
                  <input type="number" class="form-control" formControlName="originalPrice" placeholder="0.00"
                    step="0.01">
                  <small class="form-text text-muted">Optional: Original price for discount display</small>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Sub-Products *</label>
              <div class="sub-products-container" formArrayName="subProducts">
                @for (control of subProductsFormArray.controls; track $index) {
                  <div class="sub-product-item mb-2">
                    <div class="d-flex gap-2">
                      <textarea 
                        class="form-control" 
                        [formControlName]="$index"
                        rows="3"
                        [placeholder]="$index === 0 ? 'Enter sub-product (required)' : 'Enter sub-product (optional)'"></textarea>
                      @if (canDeleteSubProduct()) {
                        <button 
                          type="button" 
                          class="btn btn-outline-danger align-self-start" 
                          (click)="removeSubProduct($index)"
                          title="Remove sub-product">
                          <i class="fas fa-trash"></i>
                        </button>
                      }
                    </div>
                    @if (subProductsFormArray.at($index).invalid && subProductsFormArray.at($index).touched) {
                      <div class="invalid-feedback d-block">
                        @if ($index === 0) {
                          First sub-product is required.
                        }
                      </div>
                    }
                  </div>
                }
                <button 
                  type="button" 
                  class="btn btn-outline-primary btn-sm mt-2" 
                  (click)="addSubProduct()">
                  <i class="fas fa-plus me-1"></i>
                  Add Sub-Product
                </button>
              </div>
              <small class="form-text text-muted">First sub-product is mandatory. Additional sub-products are optional.</small>
              @if (categoryForm.get('subProducts')?.invalid && categoryForm.get('subProducts')?.touched) {
              <div class="invalid-feedback d-block">
                Please enter at least one sub-product.
              </div>
              }
            </div>

            <!-- Multilingual Content Button -->
            <div class="mb-3">
              <div class="alert alert-info py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <i class="fas fa-globe me-2"></i>
                    <strong>Add translations for Hindi, Marathi and Kannada</strong>
                  </div>
                  <button type="button" class="btn btn-outline-primary btn-sm" (click)="openMultilingualModal(multilingualModal)">
                    <i class="fas fa-edit me-1"></i>
                    Edit Translations
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" formControlName="isActive" id="isActive">
                <label class="form-check-label" for="isActive">Active</label>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    }
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="saveCategory()"
        [disabled]="categoryForm.invalid">
        <i class="fas fa-save"></i>
        {{ isEditing ? 'Update Category' : 'Create Category' }}
      </button>
    </div>
  </ng-template>

  <!-- Edit Category Modal -->
  <ng-template #editCategoryModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-edit"></i>
        Edit Category
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      @if (categoryForm) {
      <form [formGroup]="categoryForm" (ngSubmit)="saveCategory()">
        <!-- Same form content as add modal -->
        <div class="row">
          <!-- Basic Information -->
          <div class="col-12">
            <h5 class="form-section-title">Basic Information</h5>

            <div class="mb-3">
              <label class="form-label">Category Name *</label>
              <input type="text" class="form-control" formControlName="name" placeholder="Enter category name">
              @if (categoryForm.get('name')?.invalid && categoryForm.get('name')?.touched) {
              <div class="invalid-feedback">
                Category name is required and must be at least 2 characters.
              </div>
              }
            </div>

            <div class="mb-3">
              <label class="form-label">Products *</label>
              <div class="product-selection">
                @if (products.length === 0) {
                  <div class="alert alert-warning py-2">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    No products available. Please create products first.
                  </div>
                } @else {
                  <div class="product-checkboxes">
                    @for (product of products; track product.id) {
                      <div class="form-check mb-2">
                        <input 
                          class="form-check-input" 
                          type="checkbox" 
                          [id]="'product-' + product.id"
                          [value]="product.id"
                          [checked]="isProductSelected(product.id)"
                          (change)="toggleProduct(product.id, $event)">
                        <label class="form-check-label" [for]="'product-' + product.id">
                          <strong>{{ product.name }}</strong>
                          @if (product.doseType) {
                            <small class="text-muted ms-2">({{ product.doseType }})</small>
                          }
                        </label>
                      </div>
                    }
                  </div>
                  @if (categoryForm.get('productIds')?.invalid && categoryForm.get('productIds')?.touched) {
                    <div class="invalid-feedback d-block">
                      Please select at least one product.
                    </div>
                  }
                  <small class="form-text text-muted">Select one or more products for this category</small>
                }
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Price (₹) *</label>
                  <input type="number" class="form-control" formControlName="price" placeholder="0.00" step="0.01">
                  @if (categoryForm.get('price')?.invalid && categoryForm.get('price')?.touched) {
                  <div class="invalid-feedback">
                    Price is required and must be greater than 0.
                  </div>
                  }
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Original Price (₹)</label>
                  <input type="number" class="form-control" formControlName="originalPrice" placeholder="0.00"
                    step="0.01">
                  <small class="form-text text-muted">Optional: Original price for discount display</small>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Sub-Products *</label>
              <div class="sub-products-container" formArrayName="subProducts">
                @for (control of subProductsFormArray.controls; track $index) {
                  <div class="sub-product-item mb-2">
                    <div class="d-flex gap-2">
                      <textarea 
                        class="form-control" 
                        [formControlName]="$index"
                        rows="3"
                        [placeholder]="$index === 0 ? 'Enter sub-product (required)' : 'Enter sub-product (optional)'"></textarea>
                      @if (canDeleteSubProduct()) {
                        <button 
                          type="button" 
                          class="btn btn-outline-danger align-self-start" 
                          (click)="removeSubProduct($index)"
                          title="Remove sub-product">
                          <i class="fas fa-trash"></i>
                        </button>
                      }
                    </div>
                    @if (subProductsFormArray.at($index).invalid && subProductsFormArray.at($index).touched) {
                      <div class="invalid-feedback d-block">
                        @if ($index === 0) {
                          First sub-product is required.
                        }
                      </div>
                    }
                  </div>
                }
                <button 
                  type="button" 
                  class="btn btn-outline-primary btn-sm mt-2" 
                  (click)="addSubProduct()">
                  <i class="fas fa-plus me-1"></i>
                  Add Sub-Product
                </button>
              </div>
              <small class="form-text text-muted">First sub-product is mandatory. Additional sub-products are optional.</small>
              @if (categoryForm.get('subProducts')?.invalid && categoryForm.get('subProducts')?.touched) {
              <div class="invalid-feedback d-block">
                Please enter at least one sub-product.
              </div>
              }
            </div>

            <!-- Multilingual Content Button -->
            <div class="mb-3">
              <div class="alert alert-info py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <i class="fas fa-globe me-2"></i>
                    <strong>Add translations for Hindi, Marathi and Kannada</strong>
                  </div>
                  <button type="button" class="btn btn-outline-primary btn-sm" (click)="openMultilingualModal(multilingualModal)">
                    <i class="fas fa-edit me-1"></i>
                    Edit Translations
                  </button>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" formControlName="isActive" id="editIsActive">
                <label class="form-check-label" for="editIsActive">Active</label>
              </div>
            </div>
          </div>
        </div>
      </form>
      }
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="saveCategory()"
        [disabled]="categoryForm.invalid">
        <i class="fas fa-save"></i>
        Update Category
      </button>
    </div>
  </ng-template>

  <!-- Delete Confirmation Modal -->
  <ng-template #deleteCategoryModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title text-danger">
        <i class="fas fa-exclamation-triangle"></i>
        Confirm Deletion
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <div class="delete-warning">
        <i class="fas fa-exclamation-triangle fa-3x text-warning"></i>
        <h5>Are you sure you want to delete this category?</h5>
        @if (selectedCategory) {
        <div class="category-to-delete">
          <strong>{{ selectedCategory.name }}</strong>
          <p class="text-muted">Products: {{ getProductNames(getCategoryProductIds(selectedCategory)) }}</p>
        </div>
        }
        <p class="text-danger">
          <strong>Warning:</strong> This action cannot be undone. The category will be permanently removed from the
          system.
        </p>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-danger" (click)="deleteCategory(selectedCategory?.id || '')">
        <i class="fas fa-trash"></i>
        Delete Category
      </button>
    </div>
  </ng-template>

  <!-- Multilingual Content Modal -->
  <ng-template #multilingualModal let-modal>
    <div class="modal-header">
      <h4 class="modal-title">
        <i class="fas fa-globe"></i>
        Edit Multilingual Content
      </h4>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <form [formGroup]="multilingualForm" (ngSubmit)="saveMultilingualContent()">
        <!-- Language Tabs -->
        <ul class="nav nav-tabs mb-4" id="languageTabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button class="nav-link active" id="hindi-tab" data-bs-toggle="tab" data-bs-target="#hindi" type="button" role="tab">
              <i class="fas fa-flag me-1"></i>हिंदी (Hindi)
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link" id="marathi-tab" data-bs-toggle="tab" data-bs-target="#marathi" type="button" role="tab">
              <i class="fas fa-flag me-1"></i>मराठी (Marathi)
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button class="nav-link" id="kannada-tab" data-bs-toggle="tab" data-bs-target="#kannada" type="button" role="tab">
              <i class="fas fa-flag me-1"></i>ಕನ್ನಡ (Kannada)
            </button>
          </li>
        </ul>

        <div class="tab-content" id="languageTabContent">
          <!-- Hindi Tab -->
          <div class="tab-pane fade show active" id="hindi" role="tabpanel">
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Category Name (हिंदी)</label>
                  <input type="text" class="form-control" formControlName="hi_name" placeholder="Enter category name in Hindi">
                </div>
                <div class="mb-3">
                  <label class="form-label">Sub-Products (हिंदी) *</label>
                  <div class="sub-products-container" formArrayName="hi_subProducts">
                    @for (control of hiSubProductsFormArray.controls; track $index) {
                      <div class="sub-product-item mb-2">
                        <div class="d-flex gap-2">
                          <textarea 
                            class="form-control" 
                            [formControlName]="$index"
                            rows="3"
                            [placeholder]="$index === 0 ? 'Enter sub-product (required)' : 'Enter sub-product (optional)'"></textarea>
                          @if (canDeleteHiSubProduct()) {
                            <button 
                              type="button" 
                              class="btn btn-outline-danger align-self-start" 
                              (click)="removeHiSubProduct($index)"
                              title="Remove sub-product">
                              <i class="fas fa-trash"></i>
                            </button>
                          }
                        </div>
                        @if (hiSubProductsFormArray.at($index).invalid && hiSubProductsFormArray.at($index).touched) {
                          <div class="invalid-feedback d-block">
                            @if ($index === 0) {
                              First sub-product is required.
                            }
                          </div>
                        }
                      </div>
                    }
                    <button 
                      type="button" 
                      class="btn btn-outline-primary btn-sm mt-2" 
                      (click)="addHiSubProduct()">
                      <i class="fas fa-plus me-1"></i>
                      Add Sub-Product
                    </button>
                  </div>
                  <small class="form-text text-muted">First sub-product is mandatory. Additional sub-products are optional.</small>
                  @if (multilingualForm.get('hi_subProducts')?.invalid && multilingualForm.get('hi_subProducts')?.touched) {
                    <div class="invalid-feedback d-block">
                      Please enter at least one sub-product.
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Marathi Tab -->
          <div class="tab-pane fade" id="marathi" role="tabpanel">
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Category Name (मराठी)</label>
                  <input type="text" class="form-control" formControlName="mr_name" placeholder="Enter category name in Marathi">
                </div>
                <div class="mb-3">
                  <label class="form-label">Sub-Products (मराठी) *</label>
                  <div class="sub-products-container" formArrayName="mr_subProducts">
                    @for (control of mrSubProductsFormArray.controls; track $index) {
                      <div class="sub-product-item mb-2">
                        <div class="d-flex gap-2">
                          <textarea 
                            class="form-control" 
                            [formControlName]="$index"
                            rows="3"
                            [placeholder]="$index === 0 ? 'Enter sub-product (required)' : 'Enter sub-product (optional)'"></textarea>
                          @if (canDeleteMrSubProduct()) {
                            <button 
                              type="button" 
                              class="btn btn-outline-danger align-self-start" 
                              (click)="removeMrSubProduct($index)"
                              title="Remove sub-product">
                              <i class="fas fa-trash"></i>
                            </button>
                          }
                        </div>
                        @if (mrSubProductsFormArray.at($index).invalid && mrSubProductsFormArray.at($index).touched) {
                          <div class="invalid-feedback d-block">
                            @if ($index === 0) {
                              First sub-product is required.
                            }
                          </div>
                        }
                      </div>
                    }
                    <button 
                      type="button" 
                      class="btn btn-outline-primary btn-sm mt-2" 
                      (click)="addMrSubProduct()">
                      <i class="fas fa-plus me-1"></i>
                      Add Sub-Product
                    </button>
                  </div>
                  <small class="form-text text-muted">First sub-product is mandatory. Additional sub-products are optional.</small>
                  @if (multilingualForm.get('mr_subProducts')?.invalid && multilingualForm.get('mr_subProducts')?.touched) {
                    <div class="invalid-feedback d-block">
                      Please enter at least one sub-product.
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Kannada Tab -->
          <div class="tab-pane fade" id="kannada" role="tabpanel">
            <div class="row">
              <div class="col-12">
                <div class="mb-3">
                  <label class="form-label">Category Name (ಕನ್ನಡ)</label>
                  <input type="text" class="form-control" formControlName="kn_name" placeholder="Enter category name in Kannada">
                </div>
                <div class="mb-3">
                  <label class="form-label">Sub-Products (ಕನ್ನಡ) *</label>
                  <div class="sub-products-container" formArrayName="kn_subProducts">
                    @for (control of knSubProductsFormArray.controls; track $index) {
                      <div class="sub-product-item mb-2">
                        <div class="d-flex gap-2">
                          <textarea
                            class="form-control"
                            [formControlName]="$index"
                            rows="3"
                            [placeholder]="$index === 0 ? 'Enter sub-product (required)' : 'Enter sub-product (optional)'"></textarea>
                          @if (canDeleteKnSubProduct()) {
                            <button
                              type="button"
                              class="btn btn-outline-danger align-self-start"
                              (click)="removeKnSubProduct($index)"
                              title="Remove sub-product">
                              <i class="fas fa-trash"></i>
                            </button>
                          }
                        </div>
                        @if (knSubProductsFormArray.at($index).invalid && knSubProductsFormArray.at($index).touched) {
                          <div class="invalid-feedback d-block">
                            @if ($index === 0) {
                              First sub-product is required.
                            }
                          </div>
                        }
                      </div>
                    }
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm mt-2"
                      (click)="addKnSubProduct()">
                      <i class="fas fa-plus me-1"></i>
                      Add Sub-Product
                    </button>
                  </div>
                  <small class="form-text text-muted">First sub-product is mandatory. Additional sub-products are optional.</small>
                  @if (multilingualForm.get('kn_subProducts')?.invalid && multilingualForm.get('kn_subProducts')?.touched) {
                    <div class="invalid-feedback d-block">
                      Please enter at least one sub-product.
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="saveMultilingualContent()">
        <i class="fas fa-save"></i>
        Save Translations
      </button>
    </div>
  </ng-template>
</div>

